/**
 * User Detail Page
 * 
 * Main page component for displaying user detail information.
 */

import * as React from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/services/authService";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserProfileHeader } from "./components/UserProfileHeader";
import { OverviewTab } from "./components/OverviewTab";
import { AddressTab } from "./components/AddressTab";
import { IarKeanggotaanTab } from "./components/IarKeanggotaanTab";
import { UserPaymentHistory } from "./components/UserPaymentHistory";
import { SurelTeleponTab } from "./components/SurelTeleponTab";
import { PhotoKtpTab } from "./components/PhotoKtpTab";
import { KeamananTab } from "./components/KeamananTab";
import { useUserDetail } from "./hooks/useUserDetail";
import { hasSuperAdminUserLevel } from "@/utils/privileges";

interface UserDetailPageProps {
  user?: User | null;
  onViewedUserRefresh?: () => void | Promise<void>;
}

export function UserDetailPage({
  user: propUser,
  onViewedUserRefresh,
}: UserDetailPageProps = {}) {
  const { user: authUser } = useAuth();
  const [location] = useLocation();
  const [activeTab, setActiveTab] = React.useState("overview");

  // Handle hash navigation (legacy support)
  React.useEffect(() => {
    const raw = window.location.hash.replace("#", "");
    let hash =
      raw === "iar" || raw === "membership" || raw === "kartu-anggota" ? "iar-keanggotaan" : raw;
    if (hash === "ktp" || hash === "foto") hash = "foto-ktp";
    if (hash === "account") hash = "overview";
    if (hash === "ubah-email" || hash === "nomor-hp") hash = "surel-telepon";
    if (hash === "ubah-password" || hash === "hapus-pengguna") hash = "keamanan";
    if (
      hash &&
      ["overview", "alamat-korespondensi", "iar-keanggotaan", "pembayaran", "surel-telepon", "foto-ktp", "keamanan"].includes(hash)
    ) {
      setActiveTab(hash);
    }
  }, [location]);

  const user = propUser || authUser;
  const isOwnProfile = authUser?.id === user?.id;
  const isSuperAdmin = hasSuperAdminUserLevel(authUser);
  const canEditPhone = isOwnProfile || isSuperAdmin;
  const canDeleteThisUser = Boolean(authUser) && !isOwnProfile && isSuperAdmin;
  const canViewKtpTab = isOwnProfile || isSuperAdmin;
  const showKeamananTab = isOwnProfile || canDeleteThisUser;

  const { payments, paymentsLoading, paymentsError, launchpadStatus, launchpadLoading } = useUserDetail({
    userId: user?.id_client,
    callsign: user?.callsign,
    activeTab,
  });

  if (!user) {
    return (
      <div className="flex-1 space-y-4 p-6">
        <div className="text-center text-muted-foreground">Memuat data pengguna...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 space-y-6 p-6">
      <UserProfileHeader user={user} />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap">
          <TabsTrigger value="overview" className="whitespace-nowrap">Ringkasan</TabsTrigger>
          <TabsTrigger value="alamat-korespondensi" className="whitespace-nowrap">Alamat</TabsTrigger>
          <TabsTrigger value="iar-keanggotaan" className="whitespace-nowrap">IAR &amp; Keanggotaan</TabsTrigger>
          <TabsTrigger value="pembayaran" className="whitespace-nowrap">Pembayaran</TabsTrigger>
          <TabsTrigger value="surel-telepon" className="whitespace-nowrap">Kontak</TabsTrigger>
          {canViewKtpTab && (
            <TabsTrigger value="foto-ktp" className="whitespace-nowrap">Foto &amp; KTP</TabsTrigger>
          )}
          {showKeamananTab && (
            <TabsTrigger value="keamanan" className="whitespace-nowrap">Keamanan</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab user={user} />
        </TabsContent>

        <TabsContent value="alamat-korespondensi">
          <AddressTab user={user} allowEdit={isOwnProfile} />
        </TabsContent>

        <TabsContent value="iar-keanggotaan">
          <IarKeanggotaanTab user={user} />
        </TabsContent>

        <TabsContent value="pembayaran">
          <UserPaymentHistory
            user={user}
            payments={payments}
            loading={paymentsLoading}
            error={paymentsError}
            launchpadStatus={launchpadStatus}
            launchpadLoading={launchpadLoading}
          />
        </TabsContent>

        <TabsContent value="surel-telepon">
          <SurelTeleponTab
            user={user}
            isOwnProfile={isOwnProfile}
            allowEditPhone={canEditPhone}
            onPhoneSaved={onViewedUserRefresh}
          />
        </TabsContent>

        {canViewKtpTab && (
          <TabsContent value="foto-ktp">
            <PhotoKtpTab user={user} isOwnProfile={isOwnProfile} />
          </TabsContent>
        )}

        {showKeamananTab && (
          <TabsContent value="keamanan">
            <KeamananTab
              user={user}
              isOwnProfile={isOwnProfile}
              canDelete={canDeleteThisUser}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
